import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Paperclip, Download } from "lucide-react";
import PostedByAgentLine from "../club/PostedByAgentLine";

import {
  formatPositionLabel,
  formatDivisionLine,
  formatFeeBlock,
  formatRange,
  formatFoot,
  formatHeight,
  formatSalary,
  getSalaryKind,
  shouldRender,
  formatValue,
  prettyValue,
  hasAttachedDocument,
  getDocumentUrl,
} from "../../utils/tenderFormatters";

// ──────────────────────────────────────────────────────────────────────────
// Preview modal that opens when the user clicks the magnifier on a
// RequestCard.  Same data as the card but without truncation — the
// description and key requirements show in full, and there's a dedicated
// "Attached Documents" section when a tender has a document_url.
//
// The parent passes through the SAME primary action element it gave to
// the card, so the modal CTA matches whatever the role's primary flow
// is (Agent: "Pitch player", Club: "Message Club", etc.).
//
// Layout:
//   • Mobile  → full-screen overlay (h-screen, no rounded corners)
//   • Desktop → centred panel, max-w 720px, max-h 90vh, scrollable body
//
// Renders into document.body via portal so stacking contexts inside the
// card grid can't clip it. ESC closes the modal; body scroll lock keeps
// the page underneath from drifting while open.
// ──────────────────────────────────────────────────────────────────────────

// Section header inside the body — used to group fields visually
function SectionHeader({ children }) {
  return (
    <h4 className="text-[12px] font-bold text-[#242426B2] uppercase tracking-wider mb-3">
      {children}
    </h4>
  );
}

// Data-driven row section: takes an array of {label, value} and skips
// the whole section if every row would have rendered empty. This is the
// only reliable way to "hide section when nothing inside renders" — we
// can't introspect a rendered React element's output to know in advance.
function RowSection({ title, rows }) {
  const visible = (rows || []).filter((r) => shouldRender(r.value));
  if (visible.length === 0) return null;
  return (
    <div className="border-t border-[#E5E7EB] pt-4 mt-4">
      <SectionHeader>{title}</SectionHeader>
      <div className="flex flex-col gap-0.5">
        {visible.map((r) => (
          <div
            key={r.label}
            className="flex justify-between items-start gap-4 py-1.5"
          >
            <span className="text-[13px] text-[#242426B2] flex-shrink-0">
              {r.label}
            </span>
            <span className="text-[13px] text-[#0B0B0B] font-medium text-right break-words">
              {r.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Free-form section for paragraph blocks (description, key requirements,
// attached document). The caller controls the inner JSX so we just wrap
// it with the section's title + divider chrome. The `show` prop guards
// against rendering an empty header.
function ContentSection({ title, show, children }) {
  if (!show) return null;
  return (
    <div className="border-t border-[#E5E7EB] pt-4 mt-4">
      <SectionHeader>{title}</SectionHeader>
      {children}
    </div>
  );
}

function getFileName(url) {
  if (!url) return "Document";
  try {
    const u = new URL(url, window.location.origin);
    const segments = u.pathname.split("/").filter(Boolean);
    const last = segments[segments.length - 1];
    return last ? decodeURIComponent(last) : "Document";
  } catch (_) {
    return "Document";
  }
}

const RequestPreviewModal = ({
  isOpen,
  onClose,
  tender,
  primaryAction = null,
  bookmarkSlot = null,
}) => {
  // ESC closes the modal
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Body scroll lock while modal is open
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen || !tender) return null;

  const position = formatPositionLabel(tender.position);
  const fee = formatFeeBlock(tender);
  const ageRange = formatRange(tender.min_age, tender.max_age, "years");
  const divisionLine = formatDivisionLine(tender);
  const salaryLine = formatSalary(tender);
  const salaryKind = getSalaryKind(tender);
  const hasDoc = hasAttachedDocument(tender);
  const docUrl = getDocumentUrl(tender);

  // "Games played in" choice (incl. "not important") is not persisted; an
  // unset/0 count means "not important", a positive count shows with its
  // period. Avoids the misleading raw "0".
  const hasPositiveCount = (v) =>
    v != null && v !== "" && Number.isFinite(Number(v)) && Number(v) > 0;
  const gamesPlayedValue = hasPositiveCount(tender.matches_played_last_season)
    ? `${tender.matches_played_last_season} (last season)`
    : hasPositiveCount(tender.matches_played_last_half_season)
      ? `${tender.matches_played_last_half_season} (last half season)`
      : "Not important";
  const clubName = tender.club?.name;
  const clubLogo = tender.club?.logo;
  const flag = tender.division_country?.image_url;

  const overlay = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-stretch sm:items-center justify-center sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Request preview"
    >
      <div
        className="bg-white w-full sm:max-w-[720px] sm:rounded-lg max-h-screen sm:max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Sticky header */}
        <div className="flex items-start justify-between p-6 border-b border-[#E5E7EB] flex-shrink-0">
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {position && (
              <h2 className="text-2xl font-semibold capitalize text-[#0B0B0B] truncate">
                {position}
              </h2>
            )}
            {(clubLogo || clubName) && (
              <div className="flex items-center gap-2">
                {clubLogo && (
                  <img
                    src={clubLogo}
                    alt={clubName || ""}
                    className="w-7 h-7 object-contain rounded-full"
                    referrerPolicy="no-referrer"
                  />
                )}
                {clubName && (
                  <span className="text-[15px] text-[#242426] font-medium truncate">
                    {clubName}
                  </span>
                )}
              </div>
            )}
            {/* DECISION 009 — the club opens this preview from its own My
                Requests card and can go straight to "View pitches" from here,
                so the "posted by <agent>" marker has to travel with it: an
                attribution line is never dropped on a surface the club acts on.
                Renders only when the backend sends agent_attribution, which is
                null on every request a club posted itself and on every row the
                agent/lawyer surfaces receive. */}
            <PostedByAgentLine attribution={tender.agent_attribution} />
            {divisionLine && (
              <div className="flex items-center gap-2">
                <p className="text-[13px] text-[#535370]">{divisionLine}</p>
                {flag && (
                  <img
                    src={flag}
                    alt={tender.division_country?.name || ""}
                    className="w-5 h-3.5 rounded object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {bookmarkSlot}
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close preview"
            >
              <X className="w-5 h-5 text-[#0B0B0B]" />
            </button>
          </div>
        </div>

        {/* ── Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Fee — highlighted block at the top, same as on the card */}
          {shouldRender(fee.value) && (
            <div className="flex justify-between items-center p-4 bg-[#F7F7F7] rounded-lg">
              <span className="text-[14px] text-[#1A212F] font-medium">
                {fee.label}
              </span>
              <p className="text-[16px] text-[#000000] font-semibold">
                {fee.value}
              </p>
            </div>
          )}

          {/* Description — full text in the modal */}
          <ContentSection
            title="Description"
            show={shouldRender(tender.description)}
          >
            <p className="text-[14px] text-[#242426] leading-relaxed whitespace-pre-wrap break-words">
              {tender.description}
            </p>
          </ContentSection>

          {/* Key Requirements — full text in the modal */}
          <ContentSection
            title="Key Requirements"
            show={shouldRender(tender.key_requirements)}
          >
            <p className="text-[14px] text-[#242426] leading-relaxed whitespace-pre-wrap break-words">
              {tender.key_requirements}
            </p>
          </ContentSection>

          {/* Player / staff profile fields — both shapes share this
              section, rows auto-hide when not present on the tender. */}
          <RowSection
            title="Profile"
            rows={[
              { label: "Age limit", value: ageRange },
              { label: "Foot", value: formatFoot(tender.foot) },
              {
                label: "Min. Height",
                value: formatHeight(tender.minimum_height),
              },
              {
                label: "Games played",
                // The form's "Games played in" choice (incl. "not important")
                // is not persisted; an unset/0 count is how "not important"
                // arrives, so show that instead of a meaningless "0". If a
                // half-season count was set, surface it with its period.
                value: gamesPlayedValue,
              },
              {
                label: "Experience",
                value: shouldRender(tender.experience)
                  ? `${tender.experience} years`
                  : null,
              },
              {
                label: "Languages known",
                value:
                  Array.isArray(tender.language_skills) &&
                  tender.language_skills.length > 0
                    ? tender.language_skills.join(", ")
                    : formatValue(tender.language_skills),
              },
            ]}
          />

          {/* Financial details */}
          <RowSection
            title="Financial Details"
            rows={[
              {
                label: salaryKind
                  ? `${salaryKind} Monthly salary`
                  : "Monthly salary",
                value: salaryLine,
              },
              {
                label: "Loan with option to buy",
                value:
                  tender.type_of_tender === "loan" &&
                  tender.loan_with_option_to_buy
                    ? "Yes"
                    : null,
              },
            ]}
          />

          {/* Tender meta */}
          <RowSection
            title="Request Details"
            rows={[
              {
                label: "Type",
                value: prettyValue(tender.type_of_tender),
              },
              {
                label: "Category",
                value: prettyValue(tender.tender_category),
              },
              {
                label: "Recruiting for",
                value: prettyValue(tender.recruiting_for),
              },
            ]}
          />

          {/* Attached document — single document per current backend
              schema. Section hides when no doc is attached. */}
          <ContentSection title="Attached Document" show={hasDoc}>
            <a
              href={docUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-3 p-3 bg-[#F7F7F7] rounded-lg hover:bg-[#EEF1F8] transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Paperclip className="w-4 h-4 text-[#535370] flex-shrink-0" />
                <span className="text-[13px] text-[#0B0B0B] truncate">
                  {getFileName(docUrl)}
                </span>
              </div>
              <Download className="w-4 h-4 text-[#374DF5] flex-shrink-0" />
            </a>
          </ContentSection>
        </div>

        {/* ── Sticky action footer.  Same primary action as the card,
              plus a secondary Close button on the left. */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-[#E5E7EB] flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-[14px] text-[#1E1E1E] border border-[#E6E6E6] rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          {primaryAction && <div className="flex-1">{primaryAction}</div>}
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default RequestPreviewModal;
